// Smart batch scheduler: turn a list of imported posts into suggested publish
// times. Pure and unit-tested — no React, no DOM, no network. The composer feeds
// these Dates into <input type=datetime-local> and lets the user tweak them.

import { toDatetimeLocal } from "./folderImport";

const MINUTE = 60_000;

/** How many posts a day the batch should publish. The user picks this. */
export type PerDay = 1 | 2;

/** Default local minutes-of-day for each cadence: one evening slot, or midday +
 * evening. These seed the editable time pickers; the user can change them. */
const DEFAULT_SLOT_MINUTES: Record<PerDay, number[]> = { 1: [18 * 60], 2: [12 * 60, 18 * 60] };

/** The default time(s) for a cadence, as "HH:MM" strings (for the UI pickers). */
export function defaultSlotTimes(perDay: PerDay): string[] {
  return DEFAULT_SLOT_MINUTES[perDay].map((m) => {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
  });
}

/** Parse "HH:MM" → minutes from midnight, or null if it isn't a valid time. */
export function timeToMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

/** Cadence used when a caller doesn't ask for one — the gentler, safer option. */
export const DEFAULT_PER_DAY: PerDay = 1;

/**
 * Suggest `count` publish times, one per slot, starting TOMORROW (local), at the
 * requested cadence (1/day at 18:00 — the default — or 2/day at 12:00 & 18:00).
 * A slot is skipped when it is <= now, within 60s of a `taken` timestamp, or a
 * duplicate of one already produced — so no suggestion ever lands in the past or
 * clashes.
 */
export function suggestSlots(
  count: number,
  opts: { now: Date; taken?: number[]; perDay?: PerDay; slotMinutes?: number[] },
): Date[] {
  const { now, taken = [], perDay = DEFAULT_PER_DAY } = opts;
  if (count <= 0) return [];

  // The user's chosen time(s) for the cadence, or the cadence default. Sorted so
  // slots within a day are filled earliest-first regardless of input order.
  const minutes = (opts.slotMinutes && opts.slotMinutes.length > 0
    ? opts.slotMinutes
    : DEFAULT_SLOT_MINUTES[perDay]
  )
    .slice()
    .sort((a, b) => a - b);
  const nowMs = now.getTime();
  const slots: Date[] = [];

  const clashes = (ms: number): boolean =>
    taken.some((t) => Math.abs(t - ms) <= MINUTE) ||
    slots.some((s) => Math.abs(s.getTime() - ms) <= MINUTE);

  // Walk forward a day at a time from tomorrow; bounded to avoid any chance of an
  // infinite loop if every candidate is somehow rejected.
  for (let day = 1; slots.length < count && day <= count + 400; day++) {
    for (const min of minutes) {
      if (slots.length >= count) break;
      const slot = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + day,
        Math.floor(min / 60),
        min % 60,
        0,
        0,
      );
      const ms = slot.getTime();
      if (ms <= nowMs) continue;
      if (clashes(ms)) continue;
      slots.push(slot);
    }
  }

  return slots;
}

/**
 * Map each post's folder to its publish time. Posts with an explicit `schedule:`
 * keep that exact local time; the rest are filled from suggestSlots() at the
 * requested cadence, with the explicit times — plus any `alreadyScheduled` times
 * the workspace already has on the calendar — fed in as `taken` so suggestions
 * never collide with an existing post or with each other.
 */
export function buildBatchTimes(
  posts: { folder: string; scheduleIso?: string }[],
  now: Date,
  perDay: PerDay = DEFAULT_PER_DAY,
  alreadyScheduled: number[] = [],
  slotMinutes?: number[],
): Map<string, Date> {
  const out = new Map<string, Date>();
  const taken: number[] = [...alreadyScheduled];
  const needSlots: string[] = [];

  for (const post of posts) {
    const local = toDatetimeLocal(post.scheduleIso);
    // A datetime-local string ("YYYY-MM-DDTHH:mm") is parsed in local time.
    const explicit = local ? new Date(local) : null;
    if (explicit && !Number.isNaN(explicit.getTime())) {
      out.set(post.folder, explicit);
      taken.push(explicit.getTime());
    } else {
      needSlots.push(post.folder);
    }
  }

  const slots = suggestSlots(needSlots.length, { now, taken, perDay, slotMinutes });
  needSlots.forEach((folder, i) => {
    const slot = slots[i];
    if (slot) out.set(folder, slot);
  });

  return out;
}
